import {
  action, computed, makeAutoObservable, observable, observe, when,
} from 'mobx';
import {
  CategoriesTypes, Category, isMissionActive, isMissionCategorySequential, UserMissionCategoryPopulated,
} from '@gamiphy/bl/mission';
import { ChallengesWsEvents, MissionsApi } from '@gamiphy/gamix-modules/missions';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { Http } from '@gamiphy/http';
import sortBy from 'lodash/sortBy';
import { AppPrograms } from '@gamiphy/bl/app';
import { AppStore } from '../app';

class MissionsStore {
  @observable userMissions: UserMissionCategoryPopulated[] = [];

  @observable completedUserMissions: UserMissionCategoryPopulated[] = [];

  @observable categories: Category[] = [];

  @observable birthdayMissionPopupOpen = false;

  constructor() {
    makeAutoObservable(this);

    void this.pullCategories();

    when(() => UserStore.isLoggedIn, () => {
      void this.pullUserMissions();
    });

    observe(this, 'userMissions', () => {
      void this.pullCompletedUserMissions();
    });

    when(() => UserStore.isLoggedIn, () => {
      if (UserStore.user) {
        void this.wsConnect(UserStore.user.app, UserStore.user.id);
      }
    });
  }

  @computed get activeMissions(): UserMissionCategoryPopulated[] {
    return this.userMissions.filter((mission) => isMissionActive(mission));
  }

  @computed get completedSequentialMissions(): UserMissionCategoryPopulated[] {
    return this.completedUserMissions.filter((userMission) => isMissionCategorySequential(userMission));
  }

  @computed get activeCategories(): Category[] {
    return this.categories.filter((category) => category.enabled && !this.isCategoryCompleted(category));
  }

  @computed get completedParallelCategories(): Category[] {
    return this.categories.filter((category) => category.type === CategoriesTypes.parallel && this.isCategoryCompleted(category));
  }

  @computed
  public get activeMissionsPerCategory(): (category: Category) => UserMissionCategoryPopulated[] {
    return (category) => this.userMissions.filter((userMission) => isMissionActive(userMission) && userMission.mission.category.id === category.id);
  }

  @computed
  public get getMissionsByCategory(): (category?: Category) => UserMissionCategoryPopulated[] {
    return (category) => this.userMissions.filter((userMission) => userMission.mission.category.id === category?.id) ?? [];
  }

  // Check if all category missions are completed
  @computed
  public get isCategoryCompleted(): (category?: Category) => boolean {
    return (category) => !this.getMissionsByCategory(category).some((userMission) => isMissionActive(userMission));
  }

  @action
  public setBirthdayMissionPopupOpen(value: boolean): void {
    this.birthdayMissionPopupOpen = value;
  }

  @action
  async pullUserMissions(): Promise<void> {
    if (UserStore.user && AppStore.app.program === AppPrograms.points) {
      const userMissions = await MissionsApi.getUserMissions(UserStore.user.id);
      this.userMissions = sortBy(userMissions, (u) => u.mission.order);
    }
  }

  @action
  async pullCategories(): Promise<void> {
    this.categories = await MissionsApi.getCategories();
  }

  /* Completed missions response is not populated with the categroy so
   this population is necessary to be able to unify the logic wherever the missions are used.
   */
  @action
  async pullCompletedUserMissions(): Promise<void> {
    if (UserStore.user) {
      this.completedUserMissions = (await MissionsApi.getCompletedUserMissions(UserStore.user.id)).map((userMission) => ({
        ...userMission,
        mission: {
          ...userMission.mission,
          category: this.categories.find((category) => category.id === userMission.mission.category) as Category,
        },
      }));
    }
  }

  @action
  private async wsConnect(appId: string, userId: string) {
    const io = await import('socket.io-client');
    const socket = io.default(Http.getBaseUrl() || '', {
      path: '/challenges/ws',
      reconnectionAttempts: 2,
      transports: ['websocket'],
    });
    socket.on('connect', () => socket.emit('join', {
      appId,
      userId,
    }));
    socket.on(ChallengesWsEvents.userMissionUpdated, () => this.pullUserMissions());
  }
}

export default new MissionsStore();
